import React from 'react';
import { WidgetConfig } from '../interface';
import { Box, Header, Icon, Link, Table, TableProps, Tabs } from '@amzn/awsui-components-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import './FinanceCalendarWidget.scss';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { StatusType } from 'src/components/fintech-ops/sas/SasInterfaces';
import { ErrorContainer } from 'src/components/fintech-ops/FinTechOpsCommonElements';
import LoadingBar from '@amzn/awsui-chat-components/loading-bar';
import { FinanceCalendarFromS3 } from './FinanceCalendarMockData';
import { FINANCE_CALENDAR_SCENARIOS } from './FinanceCalendarData';
import { getCurrentTime, getMomentTime } from 'src/utilities/DateTimeUtilities';

const DefaultFinanceCalendarWidgetDefinition = { defaultRowSpan: 4, defaultColumnSpan: 1 };

// ── Interfaces ──────────────────────────────────────────────

interface FinanceCalendarWidgetProps {
  financeCalendarTitle: string;
  description: string;
  tableContents: FinanceCalendarWidgetContentProps;
}

export interface FinanceCalendarWidgetContentProps {
  hasData: StatusType;
  data: FinanceCalendarDataTypes[];
  highlightEvent?: { scenario: string; event: string } | null;
}

export interface FinanceCalendarDataTypes {
  scenario: string;
  table_item: FinanceCalendarDatum[];
}

interface FinanceCalendarDatum {
  date: Date;
  event: string;
  description: string;
}

// ── S3 Data Transformer ─────────────────────────────────────
export const DataFromS3ToFinanceCalendarDataType = (data: FinanceCalendarFromS3[]): FinanceCalendarDataTypes[] => {
  // Always include all defined scenarios (Guidance, MEC, OP), plus any extra from data
  const dataScenarios = [...new Set(data.map((item) => item.scenario))];
  const allScenarios = [...new Set([...FINANCE_CALENDAR_SCENARIOS, ...dataScenarios])];

  return allScenarios.map((scenario) => {
    const items = data.filter((item) => item.scenario === scenario);
    return {
      scenario,
      table_item: items
        .map((datum) => ({
          date: new Date(datum.date_time),
          event: datum.event,
          description: datum.description
        }))
        .sort((a, b) => a.date.getTime() - b.date.getTime())
    };
  });
};

// ── Widget Entry Point ──────────────────────────────────────

export const FinanceCalendarWidget = ({ financeCalendarTitle, description, tableContents }: FinanceCalendarWidgetProps): WidgetConfig => {
  return {
    definition: DefaultFinanceCalendarWidgetDefinition,
    data: {
      icon: 'list',
      title: financeCalendarTitle,
      description: description,
      header: <FinanceCalendarWidgetHeader title={financeCalendarTitle} description={description} />,
      content: <FinanceCalendarWidgetContent hasData={tableContents.hasData} data={tableContents.data} />
    }
  };
};

// ── Header ──────────────────────────────────────────────────

interface FinanceCalendarHeaderProps {
  title: string;
  description: string;
}

const FinanceCalendarWidgetHeader: React.FC<FinanceCalendarHeaderProps> = ({ title, description }) => {
  return (
    <Header
      description={description}
      actions={
        <Link external href="https://w.amazon.com/bin/view/D&S-FP&A/Calendar/StarMap/">
          DaS FP&A Starmap Wiki
        </Link>
      }
    >
      <Box variant="h2">
        <FontAwesomeIcon icon={faCalendarDays} />
        {' ' + title}
      </Box>
    </Header>
  );
};

// ── Content ─────────────────────────────────────────────────

const isToday = (date: Date): boolean => {
  return getMomentTime(date.toISOString()).isSame(getCurrentTime(), 'day');
};

const isTodayOrFuture = (date: Date): boolean => {
  return getMomentTime(date.toISOString()).isSameOrAfter(getCurrentTime(), 'day');
};

const formatDateDisplay = (date: Date): string => {
  if (isToday(date)) return 'Today';
  return getMomentTime(date.toISOString()).format('MMM D');
};

const formatTimeDisplay = (date: Date): string => {
  return getMomentTime(date.toISOString()).format('h:mm A');
};

const financeCalendarColumnDefs: TableProps.ColumnDefinition<FinanceCalendarDatum>[] = [
  {
    id: 'date',
    header: '',
    cell: (item: FinanceCalendarDatum) => (
      <Box fontWeight={isToday(item.date) ? 'bold' : 'normal'}>
        {isToday(item.date) && <FontAwesomeIcon icon={faCalendarDays} className="finance-calendar-today-icon" />}
        {formatDateDisplay(item.date)}
      </Box>
    ),
    width: 80
  },
  {
    id: 'time',
    header: '',
    cell: (item: FinanceCalendarDatum) => <Box>{formatTimeDisplay(item.date)}</Box>,
    width: 80
  },
  {
    id: 'event',
    header: '',
    cell: (item: FinanceCalendarDatum) => <Box>{item.event}</Box>
  }
];

export const FinanceCalendarWidgetContent: React.FC<FinanceCalendarWidgetContentProps> = ({ hasData, data, highlightEvent }) => {
  const [activeTab, setActiveTab] = React.useState(data[0]?.scenario || '');
  const [highlightedEvent, setHighlightedEvent] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!activeTab && data[0]?.scenario) {
      setActiveTab(data[0].scenario);
    }
  }, [data, activeTab]);

  React.useEffect(() => {
    if (!highlightEvent) return;
    setActiveTab(highlightEvent.scenario);
    setHighlightedEvent(highlightEvent.event);
    const timer = setTimeout(() => setHighlightedEvent(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightEvent]);

  if (hasData === 'loading' && data.length < 1) {
    return <LoadingSpinner />;
  } else if (hasData === 'error') {
    return <ErrorContainer />;
  } else if (hasData === 'pending') {
    return <LoadingBar variant="gen-ai" />;
  }

  const highlightColumnDefs: TableProps.ColumnDefinition<FinanceCalendarDatum>[] = financeCalendarColumnDefs.map((col) =>
    col.id === 'event'
      ? {
          ...col,
          cell: (item: FinanceCalendarDatum) => (
            <Box>
              <span className={item.event === highlightedEvent ? 'finance-calendar-event--highlight' : ''}>{item.event}</span>
            </Box>
          )
        }
      : col
  );

  const tabs = data.map((scenarioGroup) => {
    const upcomingItems = scenarioGroup.table_item.filter((item) => isTodayOrFuture(item.date));
    return {
      id: scenarioGroup.scenario,
      label: scenarioGroup.scenario,
      content: (
        <div className="finance-calendar-table">
          <Table
            variant="embedded"
            columnDefinitions={highlightColumnDefs}
            items={upcomingItems}
            empty={
              <Box textAlign="center" color="inherit" padding="l">
                <b>No upcoming events</b>
                <Box variant="p" color="inherit">
                  There are no upcoming {scenarioGroup.scenario} events scheduled.
                </Box>
              </Box>
            }
          />
        </div>
      )
    };
  });

  return <Tabs tabs={tabs} activeTabId={activeTab} onChange={({ detail }) => setActiveTab(detail.activeTabId)} />;
};
